/**
 * Evaluation API Service
 *
 * This service provides methods for fetching session evaluation data from the API.
 *
 * @module services/evaluation
 */

import {
  Evaluation,
  EvaluationReviewDetail,
  EvaluationReviewStatus,
  EvaluationReviewSummary,
  EvaluationReviewUpdate,
} from '../types/evaluation';
import { getIdToken } from './auth';

/**
 * API configuration from environment variables.
 */
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const MAX_API_ATTEMPTS = 3;
const RETRY_DELAY_MS = 250;

/**
 * Error codes for evaluation-related errors.
 */
export type EvaluationErrorCode =
  | 'FETCH_FAILED'
  | 'UPDATE_FAILED'
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

/**
 * Custom error class for evaluation-related errors.
 */
export class EvaluationError extends Error {
  statusCode: number;
  code: EvaluationErrorCode;
  originalError?: Error;

  constructor(
    message: string,
    statusCode: number,
    code: EvaluationErrorCode = 'UNKNOWN',
    originalError?: Error
  ) {
    super(message);
    this.name = 'EvaluationError';
    this.statusCode = statusCode;
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Map HTTP status codes to error codes.
 */
function getErrorCode(statusCode: number): EvaluationErrorCode {
  switch (statusCode) {
    case 400:
      return 'VALIDATION_ERROR';
    case 401:
    case 403:
      return 'UNAUTHORIZED';
    case 404:
      return 'NOT_FOUND';
    default:
      return 'FETCH_FAILED';
  }
}

function shouldRetryEvaluationRequest(error: EvaluationError): boolean {
  return error.statusCode === 0 || error.statusCode >= 500;
}

function waitForRetry(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, RETRY_DELAY_MS);
  });
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let lastError: EvaluationError | null = null;

  for (let attempt = 1; attempt <= MAX_API_ATTEMPTS; attempt += 1) {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });

      if (!response.ok) {
        const responseBody = await response.text().catch(() => '');
        throw new EvaluationError(
          `Evaluation API request failed: ${JSON.stringify({
            endpoint,
            method: options.method || 'GET',
            status: response.status,
            responseBody,
          })}`,
          response.status,
          getErrorCode(response.status)
        );
      }

      return response.json();
    } catch (error) {
      lastError = error instanceof EvaluationError
        ? error
        : new EvaluationError(
          `Network error during evaluation API request: ${JSON.stringify({
            endpoint,
            method: options.method || 'GET',
          })}`,
          0,
          'NETWORK_ERROR',
          error instanceof Error ? error : undefined
        );

      if (attempt < MAX_API_ATTEMPTS && shouldRetryEvaluationRequest(lastError)) {
        console.warn('Retrying evaluation API request', {
          endpoint,
          method: options.method || 'GET',
          attempt,
          maxAttempts: MAX_API_ATTEMPTS,
          statusCode: lastError.statusCode,
          errorCode: lastError.code,
          message: lastError.message,
        });
        await waitForRetry();
        continue;
      }

      throw lastError;
    }
  }

  throw lastError || new EvaluationError(
    `Evaluation API request failed without a response: ${JSON.stringify({
      endpoint,
      method: options.method || 'GET',
    })}`,
    0,
    'UNKNOWN'
  );
}

/**
 * Fetch evaluation data for a specific session from the API.
 *
 * @param sessionId - The ID of the session to fetch evaluation for
 * @returns Promise resolving to an Evaluation object, or null if not found
 * @throws EvaluationError if the request fails (except for 404 which returns null)
 */
export async function getEvaluation(
  sessionId: string
): Promise<Evaluation | null> {
  try {
    const token = await getIdToken();

    const response = await fetch(`${API_BASE_URL}/evaluations/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 404) {
      // Check if evaluation is pending or truly not found
      try {
        const errorData = await response.json();
        console.log('Evaluation not found:', errorData);
      } catch {
        // Ignore JSON parse errors
      }
      throw new EvaluationError(
        'Evaluation not available yet',
        404,
        'NOT_FOUND'
      );
    }

    if (!response.ok) {
      const errorMessage = await response.text().catch(() => 'Unknown error');
      const code = getErrorCode(response.status);
      throw new EvaluationError(
        errorMessage || `Request failed with status ${response.status}`,
        response.status,
        code
      );
    }

    const data = await response.json();
    
    // Transform API response to match frontend Evaluation type
    const evaluation: Evaluation = {
      sessionId: data.sessionId,
      overallScore: data.overallScore,
      categories: data.categories || [],
      feedback: data.feedback || '',
      transcriptUrl: data.transcriptUrl || '',
      evaluationUrl: data.evaluationUrl || '',
      transcriptContent: data.transcriptContent || '',
      reportContent: data.reportContent || '',
      reviewStatus: data.reviewStatus,
      evaluationReview: data.evaluationReview,
    };

    return evaluation;
  } catch (error) {
    if (error instanceof EvaluationError) {
      throw error;
    }
    // Handle network errors
    console.error('Network error fetching evaluation:', error);
    throw new EvaluationError(
      'Network error occurred while fetching evaluation data',
      0,
      'NETWORK_ERROR',
      error instanceof Error ? error : undefined
    );
  }
}

export async function getEvaluationReviews(
  status: EvaluationReviewStatus | 'all' = 'pending_review'
): Promise<EvaluationReviewSummary[]> {
  const params = new URLSearchParams({ status });
  const data = await fetchWithAuth<{ reviews: EvaluationReviewSummary[] }>(
    `/evaluations/reviews?${params.toString()}`
  );
  return data.reviews;
}

export async function getEvaluationReview(
  userId: string,
  sessionId: string
): Promise<EvaluationReviewDetail> {
  return fetchWithAuth<EvaluationReviewDetail>(
    `/evaluations/reviews/${encodeURIComponent(userId)}/${encodeURIComponent(sessionId)}`
  );
}

export async function updateEvaluationReview(
  userId: string,
  sessionId: string,
  update: EvaluationReviewUpdate
): Promise<EvaluationReviewDetail> {
  return fetchWithAuth<EvaluationReviewDetail>(
    `/evaluations/reviews/${encodeURIComponent(userId)}/${encodeURIComponent(sessionId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(update),
    }
  );
}

/**
 * Evaluation service object for convenient access to all evaluation-related functions.
 */
export const evaluationService = {
  getEvaluation,
  getEvaluationReviews,
  getEvaluationReview,
  updateEvaluationReview,
};

export default evaluationService;
